/**
 * RTK Query API Slice for Authentication
 * 
 * Handles all authentication-related endpoints including:
 * - User signin/login
 * - Token verification
 * - Password management (forgot, reset, change)
 * - Admin password reset features
 * 
 * Key architectural decisions:
 * - Uses existing apiClient as baseQuery (maintains snake_case)
 * - No caching for auth endpoints (security best practice)
 * - Consistent error handling across all endpoints
 * - Replaces existing Redux/Saga auth patterns
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import apiClientBaseQuery from './baseQuery';

const unwrapSingleItemArray = (value) => (
  Array.isArray(value) ? value[0] : value
);

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: apiClientBaseQuery,
  
  // Auth endpoints should not be cached
  keepUnusedDataFor: 0,
  refetchOnMountOrArgChange: true,
  
  // Define cache tags for invalidation
  tagTypes: ['Auth', 'User', 'TrustedDevices'],
  
  endpoints: (builder) => ({
    // User signin/login (web app uses MFA-enforced route)
    signin: builder.mutation({
      query: (credentials) => ({
        url: '/v1/users/mfa-signin',  // 2FA enforced for admin users
        method: 'POST',
        data: credentials, // Expects: { username, password }
      }),
      transformResponse: (response) => {
        // Extract actual auth data from normalized response structure
        // baseQuery returns: { data: { data: backendResponse, metadata, success } }
        // backendResponse contains: { success, message, data: { user, token }, timestamp }
        const backendResponse = unwrapSingleItemArray(
          response.data?.data || response.data || response
        );
        
        // Extract the actual auth data from backend's data field
        return backendResponse.data || backendResponse;
      },
      // Invalidate any cached user data on successful login
      invalidatesTags: ['User'],
    }),

    // User registration
    registerUser: builder.mutation({
      query: (userData) => ({
        url: '/v1/register-user',
        method: 'POST',
        data: userData, // Expects: { username, password, email, first_name, last_name, etc. }
      }),
      transformResponse: (response) => {
        return response.data?.data || response.data || response;
      },
    }),

    // REMOVED: verifyToken endpoint (WAPT-002 remediation)
    // This endpoint was unused. Apps perform client-side JWT validation instead.
    // Removal eliminates security risk of tokens in URLs.

    // Web app forgot password (email-based)
    forgotPassword: builder.mutation({
      query: (data) => ({
        url: '/v1/forgotpassword',
        method: 'POST',
        data: data, // Expects: { email } or { emailOrUsername }
      }),
      transformResponse: (response) => {
        return response.data?.data || response.data || response;
      },
    }),

    // Mobile app forgot password (username-based)
    forgotPasswordMobile: builder.mutation({
      query: (data) => ({
        url: '/v1/forgot-password',
        method: 'POST',
        data: data, // Expects: { username }
      }),
      transformResponse: (response) => {
        return response.data?.data || response.data || response;
      },
    }),

    // Reset password with token/code
    resetPassword: builder.mutation({
      query: (data) => ({
        url: '/v1/reset-password-users',
        method: 'POST',
        data: data, // Expects: { code, password }
      }),
      transformResponse: (response) => {
        return response.data?.data || response.data || response;
      },
    }),

    // Change password (authenticated users)
    changePassword: builder.mutation({
      query: (data) => ({
        url: '/v1/change-password',
        method: 'POST',
        data: data, // Expects: { user_id, old_password, new_password }
      }),
      transformResponse: (response) => {
        return response.data?.data || response.data || response;
      },
    }),

    // Admin: Set new password for users
    setNewPassword: builder.mutation({
      query: (data) => ({
        url: '/v1/set-new-password',
        method: 'POST',
        data: data, // Expects: { user_id, new_password, is_password_change }
      }),
      transformResponse: (response) => {
        return response.data?.data || response.data || response;
      },
      // Invalidate user data after password change
      invalidatesTags: ['User'],
    }),

    // ==========================================
    // Two-Factor Authentication (2FA) Endpoints
    // Security: Uses session tokens instead of user_id to prevent IDOR attacks
    // ==========================================

    // Verify 2FA code (completes authentication for admin users)
    verify2FA: builder.mutation({
      query: (data) => ({
        url: '/v1/users/verify-2fa',
        method: 'POST',
        data: data, // Expects: { two_factor_token, code }
      }),
      transformResponse: (response) => {
        const backendResponse = unwrapSingleItemArray(
          response.data?.data || response.data || response
        );
        return backendResponse.data || backendResponse;
      },
      // Invalidate any cached user data on successful 2FA verification
      invalidatesTags: ['User'],
    }),

    // Resend 2FA code (generates and sends new verification code)
    // Returns new session token that must be used for subsequent requests
    resend2FA: builder.mutation({
      query: (data) => ({
        url: '/v1/users/resend-2fa',
        method: 'POST',
        data: data, // Expects: { two_factor_token }
      }),
      transformResponse: (response) => {
        // Returns: { twoFactorToken, email }
        return unwrapSingleItemArray(response.data?.data || response.data || response);
      },
    }),

    // ==========================================
    // Trusted Device Management (NIST 800-63B Compliant)
    // Allows users to view and revoke devices trusted for 2FA bypass
    // ==========================================

    // Get list of trusted devices for the current user
    getTrustedDevices: builder.query({
      query: () => ({
        url: '/v1/users/trusted-devices',
        method: 'GET',
      }),
      transformResponse: (response) => {
        return response.data?.data || response.data || response;
      },
      providesTags: ['TrustedDevices'],
    }),

    // Revoke a specific trusted device
    revokeTrustedDevice: builder.mutation({
      query: (deviceId) => ({
        url: `/v1/users/trusted-devices/${deviceId}`,
        method: 'DELETE',
      }),
      transformResponse: (response) => {
        return response.data?.data || response.data || response;
      },
      invalidatesTags: ['TrustedDevices'],
    }),

    // Revoke ALL trusted devices for the current user
    revokeAllTrustedDevices: builder.mutation({
      query: () => ({
        url: '/v1/users/trusted-devices',
        method: 'DELETE',
      }),
      transformResponse: (response) => {
        return response.data?.data || response.data || response;
      },
      invalidatesTags: ['TrustedDevices'],
    }),

    // Logout (client-side only, clears all auth data)
    logout: builder.mutation({
      queryFn: () => {
        // Clear all auth-related storage systematically
        // This should be the ONLY place where logout cleanup happens
        
        // Clear all keys from localStorage
        const localStorageKeys = Object.keys(localStorage);
        const authRelatedKeys = localStorageKeys.filter(key => 
          // Remove any auth-related keys
          key.includes('token') || 
          key.includes('user') || 
          key.includes('admin') || 
          key.includes('client') ||
          key.includes('Admin') ||
          key === 'user_id' ||
          key === 'client_id'
        );
        authRelatedKeys.forEach(key => localStorage.removeItem(key));
        
        // Clear all keys from sessionStorage
        const sessionStorageKeys = Object.keys(sessionStorage);
        const sessionAuthKeys = sessionStorageKeys.filter(key => 
          // Remove any auth-related keys
          key.includes('token') || 
          key.includes('user') || 
          key.includes('name') ||
          key === 'username'
        );
        sessionAuthKeys.forEach(key => sessionStorage.removeItem(key));
        
        // Return success
        return { data: { success: true } };
      },
      // Invalidate all cached data on logout
      invalidatesTags: ['Auth', 'User'],
      // Also reset the entire API state on logout
      onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        // Reset all RTK Query caches
        dispatch(authApi.util.resetApiState());
      }
    }),
  }),
});

// Export hooks for use in components
export const {
  useSigninMutation,
  useRegisterUserMutation,
  // useVerifyTokenQuery, // REMOVED: Endpoint no longer exists (WAPT-002)
  useForgotPasswordMutation,
  useForgotPasswordMobileMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useSetNewPasswordMutation,
  // Two-Factor Authentication
  useVerify2FAMutation,
  useResend2FAMutation,
  // Trusted Device Management
  useGetTrustedDevicesQuery,
  useRevokeTrustedDeviceMutation,
  useRevokeAllTrustedDevicesMutation,
  useLogoutMutation,

  // For manual cache invalidation if needed
  util: { invalidateTags },
} = authApi;

export default authApi;
